// Incidents list. Members see all incidents in their org; status filter
// (triggered, acknowledged, resolved) along the top.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { ArrowRight, Bell, ExternalLink } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import { orgIncidentsCol } from '../lib/firestore';
import {
  PRIORITY_TONES,
  STATUS_LABELS,
  STATUS_TONES,
  readIncident,
} from '../lib/incidents';
import type { Incident, IncidentStatus } from '../lib/types';
import {
  AppShell,
  Badge,
  Card,
  CardBody,
  Container,
  PageHeader,
  Row,
  Spinner,
  Stack,
  Table,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

type Filter = 'open' | 'all' | IncidentStatus;

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'triggered', label: 'Triggered' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];

export default function IncidentsPage() {
  const { orgId } = useOrg();

  const [incidents, setIncidents] = useState<Incident[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('open');

  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(
      query(orgIncidentsCol(orgId), orderBy('raisedAt', 'desc')),
      (snap) => {
        setIncidents(snap.docs.map((d) => readIncident(d.data() as Parameters<typeof readIncident>[0])));
      },
      (err) => {
        console.error('[incidents] snapshot failed', err);
        setError('Could not load incidents.');
      },
    );
    return () => unsub();
  }, [orgId]);

  const filtered = useMemo(() => {
    if (!incidents) return null;
    if (filter === 'all') return incidents;
    if (filter === 'open') return incidents.filter((i) => i.status !== 'resolved');
    return incidents.filter((i) => i.status === filter);
  }, [incidents, filter]);

  if (!orgId) return null;

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title="Incidents"
          subtitle="Active and historical incidents across your services. Trigger from a service page."
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={4}>
          <Row gap={1}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`ds-button ds-button--${
                  filter === f.key ? 'secondary' : 'ghost'
                } ds-button--sm`}
              >
                {f.label}
              </button>
            ))}
          </Row>

          {filtered === null ? (
            <Row gap={2} align="center">
              <Spinner size="sm" />
              <span className="ds-text-muted ds-text-sm">Loading…</span>
            </Row>
          ) : filtered.length === 0 ? (
            <Card>
              <CardBody>
                <Row gap={3} align="center">
                  <Bell size={20} strokeWidth={1.75} color="var(--color-text-tertiary)" />
                  <Stack gap={1}>
                    <span style={{ fontWeight: 'var(--fw-medium)' }}>No incidents to show</span>
                    <span className="ds-text-muted ds-text-sm">
                      {filter === 'open'
                        ? 'Quiet right now. Trigger an incident from a service page if you want to test the flow.'
                        : 'Nothing matches this filter.'}
                    </span>
                  </Stack>
                </Row>
              </CardBody>
            </Card>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Service</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Raised</th>
                  <th style={{ textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Stack gap={1}>
                        <span style={{ fontWeight: 'var(--fw-medium)' }}>{i.title}</span>
                        {i.description && (
                          <span className="ds-text-muted ds-text-xs" style={truncateStyle}>
                            {i.description}
                          </span>
                        )}
                      </Stack>
                    </td>
                    <td className="ds-text-muted">{i.serviceName}</td>
                    <td>
                      <Badge tone={PRIORITY_TONES[i.priority]}>{i.priority}</Badge>
                    </td>
                    <td>
                      <Badge tone={STATUS_TONES[i.status]} dot>
                        {STATUS_LABELS[i.status]}
                      </Badge>
                    </td>
                    <td className="ds-table__mono">{relativeTime(i.raisedAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Row gap={1} justify="end" align="center">
                        {i.sourceExternalUrl && (
                          <a
                            href={i.sourceExternalUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            // Stop the row click (if we add one later)
                            // from also navigating into Rouser.
                            onClick={(e) => e.stopPropagation()}
                            className="ds-button ds-button--ghost ds-button--sm"
                            aria-label={`Open source alert in ${i.source}`}
                            title={`Open in ${i.source}`}
                          >
                            <ExternalLink size={12} strokeWidth={2} />
                          </a>
                        )}
                        <Link
                          to={`/incidents/${i.id}`}
                          className="ds-button ds-button--ghost ds-button--sm"
                        >
                          Open
                          <ArrowRight size={12} strokeWidth={2} />
                        </Link>
                      </Row>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Stack>
      </Container>
    </AppShell>
  );
}

const truncateStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString();
}
